import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, Bot, HardDrive, History, Mic, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { disablePush, pushEnabledHere } from "../lib/firebase";
import { Switch } from "./ui/switch";

// Pusat Persetujuan (UU PDP Pasal 5/9): every active consent in one place, each revocable with a single click.
export function ConsentCenterCard() {
  const { user, setUser } = useAuth();
  const [drive, setDrive] = useState(null);
  const [mic, setMic] = useState("unknown");
  const [push, setPush] = useState(pushEnabledHere());
  const [busy, setBusy] = useState("");
  const [log, setLog] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const aiOn = user?.settings?.ai_features !== false;

  const loadLog = () => api.get("/auth/me/consents").then((r) => setLog(r.data)).catch(() => setLog({ items: [] }));
  const recordBrowser = (kind, action, detail) => api.post("/auth/me/consents", { kind, action, detail }).then(loadLog).catch(() => {});

  useEffect(() => {
    loadLog();
    api.get("/integrations").then((r) => setDrive(!!r.data?.google?.connected)).catch(() => setDrive(false));
    navigator.permissions?.query({ name: "microphone" }).then((p) => {
      const sync = () => { setMic(p.state); if (p.state === "granted") recordBrowser("mic", "granted", "izin browser"); else if (p.state === "denied") recordBrowser("mic", "withdrawn", "diblokir di browser"); };
      sync(); p.onchange = sync;
    }).catch(() => {});
  }, []);

  const refreshUser = () => api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {});
  const revokeDrive = async () => {
    setBusy("drive");
    try { await api.delete("/integrations/google"); setDrive(false); toast.success("Akses Google Drive dicabut — token dihapus"); loadLog(); }
    catch { toast.error("Gagal memutus Google Drive"); } finally { setBusy(""); }
  };
  const revokePush = async () => {
    setBusy("push");
    try { await disablePush(); setPush(false); toast.success("Notifikasi push dimatikan di perangkat ini"); loadLog(); }
    catch { toast.error("Gagal mematikan notifikasi"); } finally { setBusy(""); }
  };
  const toggleAi = async (v) => {
    setBusy("ai");
    try { await api.put("/auth/settings", { ai_features: v }); await refreshUser(); loadLog(); toast.success(v ? "Persetujuan fitur AI diberikan kembali" : "Persetujuan fitur AI ditarik — chat & alat AI dihentikan"); }
    catch { toast.error("Gagal menyimpan"); } finally { setBusy(""); }
  };

  const Row = ({ icon: Icon, title, desc, status, action, testid }) => (
    <div className="flex items-start gap-3 rounded-xl border border-[#E7ECF3] p-3" data-testid={testid}>
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#2F6BFF]/10 text-[#2F6BFF]"><Icon size={15} /></span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-slate-900">{title} <span className="ml-1 text-[11px] font-medium text-slate-500">{status}</span></p>
        <p className="mt-0.5 text-xs text-slate-500">{desc}</p>
      </div>
      <div className="shrink-0">{action}</div>
    </div>
  );
  const revokeBtn = (onClick, id, label = "Tarik") => <button onClick={onClick} disabled={!!busy} className="rounded-lg border border-[#EF4444]/30 px-3 py-1.5 text-xs font-semibold text-[#EF4444] transition hover:bg-[#EF4444]/10 disabled:opacity-50" data-testid={id}>{label}</button>;

  return (
    <div className="mt-4 aivora-card p-6" data-testid="consent-center-card">
      <p className="flex items-center gap-2 font-semibold text-slate-900"><ShieldCheck size={16} className="text-[#2F6BFF]" /> Pusat Persetujuan</p>
      <p className="mt-1 text-xs text-slate-500">Persetujuan yang sedang aktif. Menarik persetujuan menghentikan pemrosesan terkait seketika (UU PDP Pasal 9) — Anda bisa memberikannya lagi kapan saja.</p>
      <div className="mt-4 space-y-2">
        <Row icon={Bot} title="Fitur AI" status={aiOn ? "· aktif" : "· ditarik"} testid="consent-ai"
          desc="Pesan, dokumen, dan berkas yang Anda kirim diproses penyedia model AI (OpenAI, Anthropic, Google, fal.ai) di luar Indonesia. Tanpa persetujuan ini chat & alat AI tidak dapat dipakai."
          action={<Switch checked={aiOn} onCheckedChange={toggleAi} disabled={busy === "ai"} data-testid="consent-ai-toggle" />} />
        <Row icon={HardDrive} title="Google Drive" status={drive === null ? "" : drive ? "· terhubung" : "· tidak terhubung"} testid="consent-drive"
          desc="Membaca dan menyimpan dokumen di Drive Anda atas perintah Anda. Menarik = token akses dihapus; dokumen di Drive tetap milik Anda."
          action={drive ? revokeBtn(revokeDrive, "consent-drive-revoke") : <a href="/integrations" className="text-xs font-semibold text-[#2F6BFF] hover:underline">Hubungkan</a>} />
        <Row icon={Bell} title="Notifikasi push" status={push ? "· aktif di perangkat ini" : "· nonaktif"} testid="consent-push"
          desc="Pengingat, jadwal, dan balasan asisten dikirim ke perangkat ini lewat Firebase Cloud Messaging."
          action={push ? revokeBtn(revokePush, "consent-push-revoke") : <a href="/profile#notifications" className="text-xs font-semibold text-[#2F6BFF] hover:underline">Aktifkan</a>} />
        <Row icon={Mic} title="Mikrofon & kamera" status={mic === "granted" ? "· diizinkan" : mic === "denied" ? "· ditolak" : "· ditanya saat dipakai"} testid="consent-mic"
          desc="Audio panggilan dikirim ke layanan suara realtime hanya selama panggilan berlangsung dan tidak disimpan. Izin dikelola browser: klik ikon gembok di bilah alamat → Mikrofon → Blokir untuk menariknya."
          action={<a href="https://support.google.com/chrome/answer/2693767" target="_blank" rel="noreferrer" className="text-xs font-semibold text-[#2F6BFF] hover:underline">Cara menarik</a>} />
      </div>
      <ConsentLog log={log} showAll={showAll} setShowAll={setShowAll} />
    </div>
  );
}

const KIND_LABEL = { terms: "Syarat & Privasi", ai_features: "Fitur AI", drive: "Google Drive", push: "Notifikasi push", mic: "Mikrofon" };
const SRC_LABEL = { register: "saat mendaftar", browser: "browser", google: "Google", "delete-account": "penghapusan akun", app: "Profil" };
const fmt = (iso) => new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

function ConsentLog({ log, showAll, setShowAll }) {
  const items = log?.items || [];
  const terms = log?.terms;
  const shown = showAll ? items : items.slice(0, 5);
  return (
    <div className="mt-5 border-t border-[#E7ECF3] pt-4" data-testid="consent-log">
      <p className="flex items-center gap-2 text-sm font-semibold text-slate-900"><History size={14} className="text-[#2F6BFF]" /> Log persetujuan</p>
      <p className="mt-0.5 text-xs text-slate-500">Bukti kapan tiap persetujuan diberikan atau ditarik (UU PDP Pasal 20 & 24). Ikut dalam ekspor data Anda.</p>
      {terms?.at && (
        <p className="mt-2 text-xs text-slate-600" data-testid="consent-log-terms">
          Syarat & Kebijakan Privasi v{terms.terms} disetujui {fmt(terms.at)}{terms.ip ? ` · IP ${terms.ip}` : ""}
        </p>
      )}
      {log === null ? <p className="mt-2 text-xs text-slate-400">Memuat…</p> : items.length === 0 ? (
        <p className="mt-2 text-xs text-slate-400" data-testid="consent-log-empty">Belum ada perubahan persetujuan tercatat.</p>
      ) : (
        <ul className="mt-2 space-y-1" data-testid="consent-log-list">
          {shown.map((e, i) => (
            <li key={`${e.at}-${i}`} className="flex items-center gap-2 text-xs" data-testid={`consent-log-item-${e.kind}-${e.action}`}>
              <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${e.action === "granted" ? "bg-emerald-500" : "bg-[#EF4444]"}`} />
              <span className="w-32 shrink-0 text-slate-500">{fmt(e.at)}</span>
              <span className="font-semibold text-slate-800">{KIND_LABEL[e.kind] || e.kind}</span>
              <span className={e.action === "granted" ? "text-emerald-600" : "text-[#EF4444]"}>{e.action === "granted" ? "diberikan" : "ditarik"}</span>
              <span className="truncate text-slate-400">{[SRC_LABEL[e.source] || e.source, e.detail].filter(Boolean).join(" · ")}</span>
            </li>
          ))}
        </ul>
      )}
      {items.length > 5 && (
        <button onClick={() => setShowAll(!showAll)} className="mt-2 text-xs font-semibold text-[#2F6BFF] hover:underline" data-testid="consent-log-toggle">
          {showAll ? "Tampilkan lebih sedikit" : `Tampilkan semua (${items.length})`}
        </button>
      )}
    </div>
  );
}
